/// FOURJS_START_COPYRIGHT(D,2024)
/// Property of Four Js*
/// (c) Copyright Four Js 2024, 2026. All Rights Reserved.
/// * Trademark of Four Js Development Tools Europe Ltd
///   in the United States and elsewhere
///
/// This file can be modified by licensees according to the
/// product manual.
/// FOURJS_END_COPYRIGHT

//Required by ESlint not to raise errors
"use strict";

modulum("MainContainerService", ["InitService", "StoredSettingsService"], function (context, cls) {
  /**
   * Main service dedicated to manage main areas visibility and states
   * (sidebars, chromebar, content, ...)
   * This service should stay away from any business considerations
   * and just offer simple controls on the different UI regions
   * @namespace gbc.MainContainerService
   * @gbcService
   */
  class MainContainerService /** @lends gbc.MainContainerService */ {
    constructor() {
      /**
       * @type {cls.EventListener}
       */
      this._eventListener = null;

      /**
       * Main container
       * @type {HTMLElement}
       */
      this._root = null;
      /**
       * ChromeBar container
       * @type {HTMLElement}
       */
      this._chromeBar = null;
      /**
       * ChromeBar start container
       * @type {HTMLElement}
       */
      this._chromeBarStartContainer = null;
      /**
       * ChromeBar middle container
       * @type {HTMLElement}
       */
      this._chromeBarMiddleContainer = null;

      /**
       * ChromeBar action panel
       * @type {HTMLElement}
       */
      this._chromeBarEndContainer = null;
      /**
       * SideBar rail container (thin left sidebar)
       * @type {HTMLElement}
       */
      this._sideBarRail = null;
      /**
       * True if Side bar rail is visible
       * @type {boolean}
       */
      this._isSideBarRailVisible = true;
      /**
       * True if Side bar drawer is visible
       * @type {boolean}
       */
      this._isSideBarDrawerVisible = true;
      /**
       * True if sidebar drawer is docked
       * @type {boolean}
       */
      this._isSideBarDrawerDocked = false;
      /**
       * True if floating panel is visible
       * @type {boolean}
       */
      this._isDockedPanelVisible = false;
      /**
       * True if floating panel has been undocked
       * @type {boolean}
       * @private
       */
      this._hasFloatingPanelBeenUndocked = false;
      /**
       * SideBar drawer container (second left sidebar located at the right of the sidebar rail)
       * @type {HTMLElement}
       */
      this._sideBarDrawer = null;
      /**
       * Splitter widget used to resize the SideBarDrawer or DockedPanel
       * @type {classes.SideBarSplitterWidget}
       */
      this._sideBarSplitter = null;
      /**
       * True if the sidebar buttons (dock, undock, openDrawer)
       * were initialized
       */
      this._sideBarButtonInitialized = false;

      /**
       * List of active category items displayed in sidebar drawer
       * @type {Map<string, classes.ItemWidget>}
       */
      this._activeSideBarDrawerCategoryItems = null;

      /**
       * Floating panel container displayed by sidebar button
       * @type {HTMLElement}
       */
      this._floatingPanel = null;

      /**
       * Handler to execute when floating panel is closed
       * @type {Function}
       */
      this._floatingPanelCloseHandler = null;

      /**
       * True if floating panel is visible
       * @type {boolean}
       */
      this._isFloatingPanelVisible = false;
      /**
       * Docked panel container displayed by sidebar button
       * @type {HTMLElement}
       */
      this._dockedPanel = null;
      /**
       * List of supported categories (for both floating panel and sidebar drawer), sorted by priority
       * @type {Array<string>}
       */
      this._categories = null;

      /**
       * Default ChromeBar title when empty
       * @type {string}
       */
      this._defaultChromeBarMiddleText = "Genero Browser Client";
      /**
       * Main area container
       * @type {HTMLElement}
       */
      this._main = null;
      /**
       * Content container
       * @type {HTMLElement}
       */
      this._content = null;

      /**
       * Close icon added to SideBarDrawer and FloatingPanel (only visible on mobile)
       * @type {HTMLElement}
       */
      this._closeIcon = null;

      /**
       * Dock button item used to pin the sidebar. Appended to the SideBarDrawer.
       * @type {cls.ItemWidget}
       */
      this._sideBarDrawerDockItem = null;

      /**
       * Undock button item used to unpin the sidebar. Appended to the SideBarDrawer.
       * @type {cls.ItemWidget}
       */
      this._sideBarDrawerUnDockItem = null;

      /**
       * Dock button item used to pin the floating panel. Appended to the Floating Panel.
       * @type {cls.ItemWidget}
       */
      this._floatingPanelDockItem = null;

      /**
       * Undock button item used to unpin the floating panel. Appended to the Floating Panel.
       * @type {cls.ItemWidget}
       */
      this._dockedPanelUnDockItem = null;

      /**
       * Open drawer button (used to toggle the SideBarDrawer visibility). Appended to the SideBarRail.
       * @type {cls.ItemWidget}
       */
      this._openDrawerItem = null;

      /**
       * Hidden ChromeBar containers from theme variable ("gbc-ChromeBar-hidden-containers").
       * @type {string[]}
       */
      this._hiddenChromeBarContainers = [];
    }

    /**
     * @inheritDoc
     */
    init() {
      this._eventListener = new cls.EventListener();
      this._activeSideBarDrawerCategoryItems = new Map();
      // ordered list of supported categories for both the navigation button and the sidebar drawer
      this._categories = ["menu", "back", "apps", "window", "other"];

      // Trigger the docking when the Drawer is displayed the first time.
      // Using an event avoid null reference errors by calling dock instantly,
      // and still dock properly the drawer the first time it open.
      this._eventListener.when(
        context.constants.widgetEvents.sideBarDrawerShown,
        () => {
          if (this._isSideBarDrawerDocked) {
            // Set it to false to be able to trigger the docking
            this._isSideBarDrawerDocked = false;
            this.dockSideBarDrawer();
          }
        },
        true // Do it only once
      );

      // Set the state from the storedSettings, so other services can act correctly
      this._isSideBarDrawerDocked = this.shouldAutoDockSideBarDrawer();

      this._hiddenChromeBarContainers = context.ThemeService.getValue("gbc-ChromeBar-hidden-containers")
        .trim()
        .split(/\s+/)
        .map((s) => s.toLowerCase());
    }

    /**
     * @inheritDoc
     */
    destroy() {
      if (this._floatingPanelCloseHandler) {
        this._floatingPanelCloseHandler();
        this._floatingPanelCloseHandler = null;
      }

      if (this._activeSideBarDrawerCategoryItems) {
        for (let item of this._activeSideBarDrawerCategoryItems.values()) {
          if (!item.isDestroyed()) {
            item.destroy();
          }
        }
        this._activeSideBarDrawerCategoryItems.clear();
      }

      this._destroySplitter(this.getSideBarDrawer());

      this.reset();
    }

    /**
     * Clear content of different panels (sidebar, chromebar, floating panel, ...) and reset everything
     */
    reset() {
      this.getSideBarRailContainer().replaceChildren();
      this.getSideBarDrawerContainer().replaceChildren();
      this.hideSideBarRail();
      this.hideSideBarDrawer();
      this.resetChromeBar();
    }

    /**
     * Handler user click behaviors on its different containers
     * @param {*} domEvent - mouse click event from DOM
     * @param {HTMLElement} targetElement - clicked element
     */
    manageMouseClick(domEvent, targetElement) {
      // Check if the floating panel is visible before evaluating click context
      const floatingPanelValid = this.isFloatingPanelVisible() && this.getFloatingPanel();
      if (floatingPanelValid) {
        // Hide floating panel if click occurred on the backdrop, sidebar rail, or a '.close' element
        const targetShouldClosePanel =
          this.getBackDrop().element.contains(targetElement) ||
          this.getSideBarRail().contains(targetElement) ||
          this.getSideBarDrawer().contains(targetElement) ||
          targetElement.classList.contains("close");
        if (targetShouldClosePanel) {
          this.hideFloatingPanel();
        }
      }

      // Check if the SideBarDrawer is visible and undocked before evaluating click context
      const sidebarDrawerValid = this.isSideBarDrawerVisible() && !this.isSideBarDrawerDocked() && this.getSideBarDrawer();
      if (sidebarDrawerValid) {
        // Hide drawer if click occurred on the backdrop or a '.close' element
        const targetShouldCloseDrawer =
          this.getBackDrop().element.contains(targetElement) ||
          targetElement.classList.contains("gbc_SideBarRail") ||
          targetElement.classList.contains("close");
        if (targetShouldCloseDrawer) {
          this.hideSideBarDrawer();
        }
      }
    }

    /**
     * Handler user keys pressed on its different containers
     * @param keyString
     * @param domKeyEvent
     * @param repeat
     */
    managePriorityKeyDown(keyString, domKeyEvent, repeat) {
      // close floating panel or sidebar drawer
      if (["enter", "return", "esc"].includes(keyString) && this.hasFloatingElements()) {
        // activation keys belong to a control focused inside the floating
        // element (the focused widget handles them); Esc always closes.
        // The drawer is tested regardless of docked state on purpose: this
        // guards the focused control, it is not the hasFloatingElements() set.
        const focusInFloating =
          keyString !== "esc" &&
          (this.getFloatingPanel()?.contains(document.activeElement) || this.getSideBarDrawer()?.contains(document.activeElement));
        if (!focusInFloating) {
          this.hideFloatingElements();
          return true;
        }
      }
      return false;
    }

    /**
     * Return the main container of the page (top parent div)
     * @returns {HTMLElement}
     */
    getMainContainer() {
      if (!this._root) {
        this._root = window.document.getElementsByClassName("gbc_MainContainerWidget")[0];
      }
      return this._root;
    }
    // MAIN AREA
    /**
     * Return main content area (containerElement)
     * @returns {HTMLElement}
     */
    getMainContent() {
      if (!this._content) {
        this._content = window.document.getElementsByClassName("gbc_MainContent")[0];
      }
      return this._content;
    }
    /**
     * Set main area content with html element passed as parameter. If the element is null, then we simply empty the main area.
     * @param {HTMLElement} content
     */
    setMainContent(content) {
      this.getMainContent().replaceChildren(content);
    }

    // CHROMEBAR
    getChromeBar() {
      if (!this._chromeBar) {
        this._chromeBar = window.document.getElementsByClassName("gbc_ChromeBar")[0];
      }
      return this._chromeBar;
    }
    /**
     * Return chromebar start container (which contains chromebar navigation button)
     * @returns {HTMLElement}
     */
    getChromeBarStartContainer() {
      if (!this._chromeBarStartContainer) {
        this._chromeBarStartContainer = window.document.getElementsByClassName("gbc_ChromeBar__StartContainer")[0];
      }
      if (this._hiddenChromeBarContainers.includes("start")) {
        this._chromeBarStartContainer.classList.add("hidden");
      }
      return this._chromeBarStartContainer;
    }
    /**
     * Set chromebar middle container content with html element passed as parameter. If parameter is null, then we simply remove the title
     * @param {HTMLElement?} content
     */
    setChromeBarStartContent(...content) {
      if (!this.getChromeBar()) {
        return;
      }
      if (content?.length) {
        this.getChromeBarStartContainer().replaceChildren(...content);
      } else {
        this.getChromeBarStartContainer().replaceChildren();
      }
    }
    /**
     * Return chromebar middle container
     * @returns {HTMLElement}
     */
    getChromeBarMiddleContainer() {
      if (!this._chromeBarMiddleContainer) {
        this._chromeBarMiddleContainer = window.document.getElementsByClassName("gbc_ChromeBar__MiddleContainer")[0];
      }
      if (this._hiddenChromeBarContainers.includes("middle")) {
        this._chromeBarMiddleContainer.classList.add("hidden");
      }
      return this._chromeBarMiddleContainer;
    }
    /**
     * Set chromebar middle container content with html element passed as parameter. If parameter is null, then we simply remove the title
     * @param {HTMLElement?} content
     */
    setChromeBarMiddleContent(...content) {
      if (!this.getChromeBar()) {
        return;
      }
      this._middleContainerListWrapper = null;
      if (content?.length) {
        this.getChromeBarMiddleContainer().replaceChildren(...content);
      } else {
        // default ending page title
        const span = document.createElement("span");
        span.textContent = this._defaultChromeBarMiddleText;
        span.style.marginLeft = "24px";
        this.getChromeBarMiddleContainer().replaceChildren(span);
      }
    }

    /**
     * Add one or multiple elements in chromebar middle container
     * @param {HTMLElement} elements
     */
    addChromeBarMiddleContainerItems(...elements) {
      // wrap elements into UL list container to improve accessibility
      if (!this._middleContainerListWrapper) {
        this._middleContainerListWrapper = document.createElement("ul");
        // Only used by ApplicationListService when applicationListPosition === "top": each app is an
        // exclusive "current window" trigger → tablist pattern (each app <li> wraps a <button role="tab">).
        this._middleContainerListWrapper.setAttribute("role", "tablist");
        this._middleContainerListWrapper.setAttribute("aria-label", "Open applications");
        this.getChromeBarMiddleContainer().append(this._middleContainerListWrapper);
      }
      this._middleContainerListWrapper.append(...elements);
    }
    /**
     * Remove element from chromebar middle container
     * @param {HTMLElement} element
     */
    removeChromeBarMiddleContainerItem(element) {
      element.remove();
    }

    /**
     * Return chromebar end container
     * @returns {HTMLElement}
     */
    getChromeBarEndContainer() {
      if (!this._chromeBarEndContainer) {
        this._chromeBarEndContainer = window.document.getElementsByClassName("gbc_ChromeBar__EndContainer")[0];
      }
      return this._chromeBarEndContainer;
    }

    /**
     * Add one or multiple elements in chromebar end container
     * @param {HTMLElement} elements
     */
    addChromeBarEndContainerItems(...elements) {
      this.getChromeBarEndContainer().append(...elements);
    }
    /**
     * Remove element from chromebar end container
     * @param {HTMLElement} element
     */
    removeChromeBarEndContainerItem(element) {
      element.remove();
    }
    /**
     * Set the whole content of chromebar end container
     * @param {HTMLElement} elements
     */
    setChromeBarEndContent(...elements) {
      if (elements) {
        this.getChromeBarEndContainer().replaceChildren(...elements);
      } else {
        this.getChromeBarEndContainer().replaceChildren();
      }
    }

    /**
     * Hide the whole ChromeBar
     */
    hideChromeBar() {
      if (!this.getChromeBar()) {
        return;
      }
      this.getChromeBar().remove();
      this._chromeBar = null;
    }
    /**
     * Returns true if ChromeBar is visible
     */
    isChromeBarVisible() {
      return Boolean(this.getChromeBar());
    }
    /**
     * Empty all the different sections of the ChromeBar
     */
    resetChromeBar() {
      if (!this.getChromeBar()) {
        return;
      }
      this.setChromeBarStartContent();
      this.setChromeBarMiddleContent();
      this.setChromeBarEndContent();
    }
    // SIDEBARS
    // SIDEBAR RAIL
    /**
     * Return sidebar rail area
     * @returns {HTMLElement}
     */
    getSideBarRail() {
      if (!this._sideBarRail) {
        this._sideBarRail = window.document.getElementsByClassName("gbc_SideBarRail")[0];
      }
      return this._sideBarRail;
    }

    /**
     * Return sidebar rail container
     * Where the children will be added
     * @returns {HTMLElement}
     */
    getSideBarRailContainer() {
      if (!this._sideBarRailContainer) {
        this._sideBarRailContainer = this.getSideBarRail().getElementsByClassName("gbc_SideBarRail__container")[0];
      }
      return this._sideBarRailContainer;
    }

    /**
     * Return true if sidebar rail is currently visible
     * @return {boolean}
     */
    isSideBarRailVisible() {
      return this._isSideBarRailVisible;
    }
    /**
     * Return true if sidebar rail is empty
     * @return {boolean}
     */
    isSideBarRailEmpty() {
      const sideBarRailContainer = context.MainContainerService.getSideBarRailContainer();
      // If the container doesn't exist, consider it empty
      if (!sideBarRailContainer) {
        return true;
      }
      return sideBarRailContainer.children.length === 0;
    }
    /**
     * Display sidebar rail
     */
    showSideBarRail() {
      this.getSideBarRail().classList.remove("hidden");
      if (this.isSideBarRailVisible()) {
        return;
      }
      this.getMainContainer().append(this.getSideBarRail());
      this.getSideBarDrawer().classList.remove("align-left");
      if (this.isSideBarDrawerEmpty()) {
        this.hideOpenDrawerItem();
      }
      this._isSideBarRailVisible = true;
      this._eventListener.emit(context.constants.widgetEvents.sideBarRailShown);
    }
    /**
     * Hide sidebar rail
     */
    hideSideBarRail() {
      if (!this.isSideBarRailVisible()) {
        return;
      }
      this.getSideBarRail().remove();
      this.getSideBarDrawer().classList.add("align-left"); // sidebar drawer get sidebar rail left alignment
      this._isSideBarRailVisible = false;
      this._eventListener.emit(context.constants.widgetEvents.sideBarRailHidden);
    }
    /**
     * Add one or multiple elements in sidebar rail
     * @param {HTMLElement} elements
     */
    addSideBarRailItems(...elements) {
      this.getSideBarRailContainer().append(...elements);
      this._eventListener.emit(context.constants.widgetEvents.sideBarRailUpdated);
    }

    // SIDEBAR DRAWER
    /**
     * @param {string} category - category name (e.g. "apps", "other", ...)
     * @returns {string} DOM id of the sidebar drawer category section for the given category
     */
    getSideBarDrawerCategoryId(category) {
      return "gbc_SideBarDrawer__category_" + this.getFilteredCategoryName(category);
    }

    /**
     * Return sidebar drawer area
     * @returns {HTMLElement}
     */
    getSideBarDrawer() {
      if (!this._sideBarDrawer) {
        this._sideBarDrawer = globalThis.document.getElementsByClassName("gbc_SideBarDrawer")[0];
        // Customizations may override the main template without keeping the id. aria-controls
        // requires a non-empty id (DOMElementHelper.updateAttribute strips the attribute when
        // value is ""), so we restore a stable fallback here.
        if (this._sideBarDrawer && !this._sideBarDrawer.id) {
          this._sideBarDrawer.id = "gbc_SideBarDrawer";
        }
      }
      return this._sideBarDrawer;
    }
    /**
     * Return sidebar drawer container
     * where the children will be added
     * @returns {HTMLElement}
     */
    getSideBarDrawerContainer() {
      if (!this._sideBarDrawerContainer) {
        this._sideBarDrawerContainer = this.getSideBarDrawer().getElementsByClassName("gbc_SideBarDrawer__container")[0];
      }
      return this._sideBarDrawerContainer;
    }

    /**
     * Checks whether the SideBarDrawer contains any child elements that are visible
     * @returns {boolean}
     */
    isSideBarDrawerEmpty() {
      const sideBarDrawerContainer = context.MainContainerService.getSideBarDrawerContainer();
      // If the container doesn't exist, consider it empty
      if (!sideBarDrawerContainer) {
        return true;
      }

      // check visible existing categories
      for (const category of this._activeSideBarDrawerCategoryItems.keys()) {
        const categoryContainer = this.getSideBarDrawerCategory(category);
        if (categoryContainer && !categoryContainer.classList.contains("hidden")) {
          return false;
        }
      }
      return true;
    }

    /**
     * Return true if sidebar drawer is currently visible
     * @return {boolean}
     */
    isSideBarDrawerVisible() {
      return this._isSideBarDrawerVisible;
    }
    /**
     * Display sidebar drawer
     */
    showSideBarDrawer() {
      if (this.isSideBarDrawerVisible()) {
        return;
      }
      // insert in beginning to support siblings CSS selectors condition
      this.getMainContainer().prepend(this.getSideBarDrawer());
      cls.DOMElementHelper.toggleClasses(this.getSideBarDrawer(), !this.isSideBarRailVisible(), "align-left");
      if (this.isSideBarDrawerDocked()) {
        this._initSplitter(this.getSideBarDrawer());
      } else {
        this.showBackDrop("sidebardrawer");
      }
      // add close button and backdropdown specifically for mobile
      if (cls.WindowHelper.isMobile()) {
        this.getSideBarDrawer().append(this.getCloseIcon());
      }
      if (this.isSideBarDrawerDockOnly() || this.shouldAutoDockSideBarDrawer()) {
        this.dockSideBarDrawer();
      } else if (!this.isDockedPanelVisible()) {
        this.hideFloatingPanel(); // Hide the floating panel to avoid overlap with sidebar drawer when sidebar drawer is floating
      }
      this.getSideBarDrawer().classList.remove("hidden");
      // In popup (non-docked) mode the drawer is a real modal dialog — match role="dialog" with aria-modal.
      // In docked mode dockSideBarDrawer() has already stripped role + aria-modal.
      if (!this.isSideBarDrawerDocked()) {
        this.getSideBarDrawer().setAttribute("aria-modal", "true");
      }
      this._isSideBarDrawerVisible = true;
      this._openDrawerItem?.setAriaAttribute("expanded", true);
      this._eventListener.emit(context.constants.widgetEvents.sideBarDrawerShown);
    }
    /**
     * Hide sidebar drawer
     */
    hideSideBarDrawer() {
      // can't hide sidebar drawer if it's docked
      if (!this.isSideBarDrawerVisible()) {
        return;
      }
      if (!this.isDockedPanelVisible()) {
        this._destroySplitter();
      }
      // Strip aria-modal before detaching so the cached element keeps a clean attribute state
      // for the next showSideBarDrawer() call (re-set only in non-docked mode).
      this.getSideBarDrawer().removeAttribute("aria-modal");
      this.getSideBarDrawer().remove();
      this.hideBackDrop("sidebardrawer");
      if (cls.WindowHelper.isMobile()) {
        this.getCloseIcon().remove();
      }

      this._isSideBarDrawerVisible = false;
      this._openDrawerItem?.setAriaAttribute("expanded", false);
      this._eventListener.emit(context.constants.widgetEvents.sideBarDrawerHidden);
    }

    /**
     * Toggle the visibility of the SideBarDrawer
     */
    toggleSideBarDrawer() {
      if (this.isSideBarDrawerVisible()) {
        this.hideSideBarDrawer();
      } else {
        this.showSideBarDrawer();
      }
    }

    /**
     * Return true if sidebar drawer is docked
     * @return {boolean}
     */
    isSideBarDrawerDocked() {
      return this._isSideBarDrawerDocked;
    }

    /**
     * Determines whether the SideBarDrawer should be auto docked
     * User stored settings take priority over the theme value
     * @returns {boolean} True if the SideBarDrawer should be docked
     */
    shouldAutoDockSideBarDrawer() {
      // no docking possibilities under mobile
      if (cls.WindowHelper.isMobile()) {
        return false;
      }
      const themeLeftDock = context.ThemeService.getValue("gbc-MainContainerWidget-left-dock").trim().toLowerCase();
      if (context.StoredSettingsService.areEnabled()) {
        const stored = context.StoredSettingsService.readGbcSettings(context.constants.storedSettingKeys.sidebarDrawerDocked);
        if (stored === null) {
          return themeLeftDock === "sidebardrawer";
        }
        return Boolean(stored);
      }
      return themeLeftDock === "sidebardrawer";
    }

    /**
     * Return true if sidebar drawer is in 'docked only' mode
     * @return {boolean}
     * @private
     */
    isSideBarDrawerDockOnly() {
      // no docking possibilities under mobile
      if (cls.WindowHelper.isMobile()) {
        return false;
      }
      const dockedOnlyMode = context.ThemeService.getValue("gbc-SideBarDrawer-display-mode").trim().toLowerCase();
      return dockedOnlyMode === "docked";
    }

    /**
     * Return true if sidebar drawer is in 'floating only' mode
     * @return {boolean}
     * @private
     */
    isSideBarDrawerFloatingOnly() {
      const dockedOnlyMode = context.ThemeService.getValue("gbc-SideBarDrawer-display-mode").trim().toLowerCase();
      return dockedOnlyMode === "floating";
    }

    /**
     * Determines whether the Floating Panel (topmenu) should be auto docked
     * @returns {boolean} True if the Floating Panel should be docked
     */
    shouldAutoDockFloatingPanel() {
      // no docking possibilities under mobile
      if (cls.WindowHelper.isMobile()) {
        return false;
      }
      const themeLeftDock = context.ThemeService.getValue("gbc-MainContainerWidget-left-dock").trim().toLowerCase();
      return themeLeftDock === "topmenu" && !this._hasFloatingPanelBeenUndocked;
    }
    /**
     * Dock sidebar drawer to left (replaces sidebar rail)
     * Also store the docking state in the stored settings
     * - hide the sidebar rail
     * - hide the backdrop
     */
    dockSideBarDrawer() {
      if (this.isSideBarDrawerDocked() || this.isSideBarDrawerFloatingOnly()) {
        return;
      }
      this.unDockFloatingPanel();
      const sideBarDrawer = this.getSideBarDrawer();
      sideBarDrawer.classList.add("docked");
      // When docked, the drawer is persistent navigation integrated in the layout — not a popup.
      // Strip the dialog role + aria-modal so assistive tech announces it as a regular complementary aside.
      sideBarDrawer.removeAttribute("role");
      sideBarDrawer.removeAttribute("aria-modal");
      // Drawer no longer behaves as a popup → clear aria-haspopup on the rail trigger.
      this._openDrawerItem?.setAriaAttribute("haspopup", null);
      sideBarDrawer.style.width = `${gbc.StoredSettingsService.loadSideBarWidth()}px`;
      this._isSideBarDrawerDocked = true;
      this.hideBackDrop("sidebardrawer");

      // Store the state
      context.StoredSettingsService.writeGbcSettings(context.constants.storedSettingKeys.sidebarDrawerDocked, this._isSideBarDrawerDocked);

      this._eventListener.emit(context.constants.widgetEvents.sideBarDrawerDocked);

      this._initSplitter(this.getSideBarDrawer());

      // need a relayout since we update available width
      context.SessionService.getCurrent()?.getCurrentApplication()?.scheduler.layoutCommand({ resize: true });
    }
    /**
     * Undock sidebar drawer and keep it displayed
     * Also store the docking state in the stored settings
     * - show the sidebar rail
     * - Show the backdrop
     */
    unDockSideBarDrawer() {
      if (!this.isSideBarDrawerDocked()) {
        return;
      }
      this.getSideBarDrawer().classList.remove("docked");
      // Back to popup mode — restore the dialog role + aria-modal to match aria-haspopup="dialog" on triggers.
      this.getSideBarDrawer().setAttribute("role", "dialog");
      this.getSideBarDrawer().setAttribute("aria-modal", "true");
      this._openDrawerItem?.setAriaAttribute("haspopup", "dialog");
      this.getSideBarDrawer().style.removeProperty("width");
      this._destroySplitter();

      if (this.isSideBarDrawerDockOnly()) {
        this.hideSideBarDrawer();
      } else {
        this.showBackDrop("sidebardrawer");
        if (cls.WindowHelper.isMobile()) {
          this.getSideBarDrawer().append(this.getCloseIcon());
        }
      }
      this._isSideBarDrawerDocked = false;

      context.StoredSettingsService.writeGbcSettings(context.constants.storedSettingKeys.sidebarDrawerDocked, this._isSideBarDrawerDocked);
      this._eventListener.emit(context.constants.widgetEvents.sideBarDrawerUndocked);
      // need a relayout since we update available width
      context.SessionService.getCurrent()?.getCurrentApplication()?.scheduler.layoutCommand({ resize: true });
    }
    /**
     * Add an element in sidebar drawer category
     * @param {string} category
     * @param {HTMLElement} element
     */
    addSideBarDrawerItems(category, element) {
      let container = this.getSideBarDrawerCategory(category);
      if (!container) {
        container = this.createSideBarDrawerCategory(category);
      }

      const isListElement = element.tagName === "LI";

      if (isListElement) {
        // <li> children must live inside a <ul> to keep a valid HTML content model. The category title
        // <button> stays as a direct child of the <section>, alongside the <ul> (sibling, not parent).
        let listWrapper = container.getElementsByTagName("ul")[0];
        if (!listWrapper) {
          listWrapper = document.createElement("ul");
          container.append(listWrapper);
        }
        listWrapper.append(element);
      } else {
        container.append(element);
      }
      this._eventListener.emit(context.constants.widgetEvents.sideBarDrawerUpdated);
    }
    /**
     * Remove element from sidebar drawer
     * @param {string} category
     * @param {HTMLElement} element
     */
    removeSideBarDrawerItem(category, element) {
      element.remove();
      let container = this.getSideBarDrawerCategory(category);
      // category is empty when its only remaining direct children are the title button and/or an empty <ul>.
      const stillHasItems = Array.from(container.children).some(
        (child) => !child.classList.contains("title") && (child.tagName !== "UL" || child.children.length > 0)
      );
      if (!stillHasItems) {
        // if we remove last element of a category, remove the category as well
        container.remove();
        const cat = this.getFilteredCategoryName(category);
        this._activeSideBarDrawerCategoryItems.get(cat).destroy();
        this._activeSideBarDrawerCategoryItems.delete(cat);
        this._updateOtherCategorySeparator();
        this._eventListener.emit(context.constants.widgetEvents.sideBarDrawerUpdated);
      }
    }

    /**
     * Create and returns a new category div container in sidebar drawer
     * @param {string} category
     * @return {HTMLElement}
     */
    getSideBarDrawerCategory(category) {
      const cat = this.getFilteredCategoryName(category);
      return this.getSideBarDrawerContainer()?.getElementsByClassName(cat)[0];
    }
    /**
     * Create and returns a new category div container in sidebar drawer
     * @param {string} category
     * @return {HTMLElement}
     */
    createSideBarDrawerCategory(category) {
      const domElement = document.createElement("section");
      // if category doesn't exist in our array of categories, then consider it as "other" category
      const cat = this.getFilteredCategoryName(category);
      domElement.classList.add("gbc_SideBarDrawer__category", cat);
      domElement.id = this.getSideBarDrawerCategoryId(cat);
      const categoryItem = cls.WidgetFactory.createWidget("Item", {
        appHash: gbc.systemAppId
      });
      categoryItem.addClasses("title");
      if (category === "apps") {
        categoryItem.setText(gbc.I18NService.t("gwc.main.sidebar.activeApps"));
      } else {
        categoryItem.setText(category);
      }
      categoryItem.setPrimaryIcon("zmdi-chevron-down");
      categoryItem.setSecondaryIconText("1");
      categoryItem.onClick(() => this.toggleCategoryItem(cat));
      categoryItem.setAriaAttribute("controls", domElement.id);
      categoryItem.setAriaAttribute("expanded", true);
      // aria-labelledby must reference a real element id — point to the category title widget root.
      domElement.setAttribute("aria-labelledby", categoryItem.getElement().id);
      domElement.append(categoryItem.getElement());
      this.getSideBarDrawerContainer()?.append(domElement);
      // If StartMenu is present in the sidebar drawer, force it to collapse
      if (this.getSideBarDrawerActiveCategories().has("other")) {
        this.toggleAppsCategoryItem(true);
      }

      this._activeSideBarDrawerCategoryItems.set(cat, categoryItem);

      this._updateOtherCategorySeparator();
      return domElement;
    }

    /**
     * Add a separator to the "other" category, but only if it is not the only category in SideBarDrawer
     */
    _updateOtherCategorySeparator() {
      const otherCategory = this.getSideBarDrawerCategory("other");
      if (!otherCategory) {
        return;
      }

      // Filter out the "dock" category and any category that has the "hidden" class
      const activeCategories = Array.from(this._activeSideBarDrawerCategoryItems.keys()).filter(
        (cat) => !this.getSideBarDrawerCategory(cat)?.classList.contains("hidden")
      );

      if (activeCategories.length > 1) {
        otherCategory.classList.add("separator");
      } else {
        otherCategory.classList.remove("separator");
      }
    }

    /**
     * Returns the DOM element corresponding to the title of a sidebar drawer category
     * @param {string} category
     * @returns {HTMLElement|null}
     */
    getSideBarDrawerCategoryTitle(category) {
      return this.getSideBarDrawerCategory(category)?.getElementsByClassName("title")[0] || null;
    }

    /**
     * Returns the active categories being displayed in sidebar drawer
     * @return {Map<string, classes.ItemWidget>}
     */
    getSideBarDrawerActiveCategories() {
      return this._activeSideBarDrawerCategoryItems;
    }

    /**
     * Sets the visibility of a specific category in the SideBarDrawer
     *
     * @param {string} category - The name of the category to modify
     * @param {string} visibility - The desired visibility state ("visible" or "hidden")
     */
    setSideBarDrawerCategoryVisibility(category, visibility) {
      const container = this.getSideBarDrawerCategory(category);
      if (!container) {
        return;
      }
      cls.DOMElementHelper.toggleClass(container, "hidden", visibility === "hidden");
      this._updateOtherCategorySeparator();
    }

    /**
     * Toggle the "apps" category collapsed/expanded state
     * @param {boolean} [collapse]
     *   - true = force collapse
     *   - false = force expand
     *   - undefined = toggle current state
     */
    toggleAppsCategoryItem(collapse) {
      this.toggleCategoryItem("apps", collapse);
    }

    /**
     * Toggle the collapsed/expanded state of a sidebar drawer category
     * @param {string} category - category name (e.g. "apps", "other", "menu", ...)
     * @param {boolean} [collapse]
     *   - true = force collapse
     *   - false = force expand
     *   - undefined = toggle current state
     */
    toggleCategoryItem(category, collapse) {
      const cat = this.getFilteredCategoryName(category);
      const domElement = this.getSideBarDrawerCategory(cat);
      const categoryItem = this._activeSideBarDrawerCategoryItems.get(cat);
      if (!domElement || !categoryItem) {
        return;
      }

      const cssClass = context.constants.widgetCssClasses.collapsed;

      let shouldCollapse = false;
      if (typeof collapse === "boolean") {
        shouldCollapse = collapse;
      } else {
        shouldCollapse = !domElement.classList.contains(cssClass);
      }

      cls.DOMElementHelper.toggleClasses(domElement, shouldCollapse, cssClass);
      if (shouldCollapse) {
        categoryItem.setPrimaryIcon("zmdi-chevron-right");
      } else {
        categoryItem.setPrimaryIcon("zmdi-chevron-down");
      }
      categoryItem.setAriaAttribute("expanded", !shouldCollapse);
    }

    /**
     * Filter the category name by checking if it exists in supported list
     * @param category
     * @return {string}
     */
    getFilteredCategoryName(category) {
      return this._categories.indexOf(category) !== -1 ? category : cls.ArrayHelper.last(this._categories);
    }

    /**
     * Init the dock, undock and open drawer buttons
     * It's a singleton pattern. This method can be ran only once
     */
    initSideBarButtons() {
      // Open Drawer item
      if (this._sideBarButtonInitialized) {
        return;
      }

      this._openDrawerItem = cls.WidgetFactory.createWidget("Item", {
        appHash: gbc.systemAppId
      });
      this._openDrawerItem.setPrimaryIcon("zmdi-apps");
      this._openDrawerItem.addClasses("gbc_ItemWidget--openDrawerButton");
      this._openDrawerItem.setQAInfo("name", "gbc_openDrawerButton");

      this._openDrawerItem.onClick(this.toggleSideBarDrawer.bind(this));

      // icon-only button: name it for assistive technologies (open/closed state comes from aria-expanded)
      this._openDrawerItem.setAriaAttribute("label", "Applications drawer");
      this._openDrawerItem.setAriaAttribute("controls", this.getSideBarDrawer().id);
      this._openDrawerItem.setAriaAttribute("expanded", this.isSideBarDrawerVisible());
      // The drawer is a popup dialog whenever it is not docked — keep aria-haspopup in sync.
      if (!this.isSideBarDrawerDocked()) {
        this._openDrawerItem.setAriaAttribute("haspopup", "dialog");
      }

      // The openDrawer button sits at the top of the rail, above the <ul> that holds the
      // launched app <li>s. Prepending directly to the <nav> root is safe regardless of how
      // customizations may have restructured the rail's inner DOM.
      this.getSideBarRail().prepend(this._openDrawerItem.getElement());

      if (!this.isSideBarDrawerFloatingOnly()) {
        // SideBarDrawer Dock Button
        this._sideBarDrawerDockItem = cls.GbcItemWidgetFactory.dockDrawer({ appHash: gbc.systemAppId });
        this.getSideBarDrawer().append(this._sideBarDrawerDockItem.getElement());

        // SideBarDrawer Undock button
        this._sideBarDrawerUnDockItem = cls.GbcItemWidgetFactory.undockDrawer({ appHash: gbc.systemAppId });
        this.getSideBarDrawer().append(this._sideBarDrawerUnDockItem.getElement());
      }

      // FloatingPanel Dock Button
      this._floatingPanelDockItem = cls.GbcItemWidgetFactory.dockFloatingPanel({ appHash: gbc.systemAppId });

      // FloatingPanel Undock button
      this._dockedPanelUnDockItem = cls.GbcItemWidgetFactory.undockFloatingPanel({ appHash: gbc.systemAppId });

      this._sideBarButtonInitialized = true;
    }

    /**
     * Initialize the splitter for the element passed as parameter and attach drag event listeners
     * @param {HTMLElement} element
     * @private
     */
    _initSplitter(element) {
      if (this._sideBarSplitter) {
        return;
      }
      this._sideBarSplitter = cls.WidgetFactory.createWidget("SideBarSplitter", {
        appHash: gbc.systemAppId
      });
      this._sideBarSplitter.setSideBarElement(element);
      // on drag end relayout
      this._sideBarSplitter.when(context.constants.widgetEvents.splitterEnd, () =>
        context.SessionService.getCurrent()?.getCurrentApplication()?.scheduler.layoutCommand({ resize: true })
      );
      element.parentNode.append(this._sideBarSplitter.getElement());
    }

    /**
     * Destroy the splitter previously added on an element
     * @private
     */
    _destroySplitter() {
      this._sideBarSplitter?.destroy();
      this._sideBarSplitter = null;
    }

    // DOCKED PANEL
    /**
     * Return the docked panel area
     * @returns {HTMLElement}
     */
    getDockedPanel() {
      if (!this._dockedPanel) {
        this._dockedPanel = globalThis.document.getElementsByClassName("gbc_DockedPanel")[0];
        cls.DOMElementHelper.updateQAAttribute(this._dockedPanel, "name", "gbc_chromeBarDockedPanel");
        // The sidebar splitter's aria-controls needs a non-empty id; customizations may drop the one from
        // the main template, so restore a stable fallback here (mirrors getSideBarDrawer).
        if (this._dockedPanel && !this._dockedPanel.id) {
          this._dockedPanel.id = "gbc_DockedPanel";
        }
      }
      return this._dockedPanel;
    }

    // FLOATING PANEL
    /**
     * Return the floating panel area
     * @returns {HTMLElement}
     */
    getFloatingPanel() {
      if (!this._floatingPanel) {
        this._floatingPanel = globalThis.document.getElementsByClassName("gbc_FloatingPanel")[0];
        cls.DOMElementHelper.updateQAAttribute(this._floatingPanel, "name", "gbc_chromeBarFloatingPanel");
        // Same fallback rationale as getSideBarDrawer — customizations may override the
        // template without an id, which would break aria-controls referencing this panel.
        if (this._floatingPanel && !this._floatingPanel.id) {
          this._floatingPanel.id = "gbc_FloatingPanel";
        }
      }
      return this._floatingPanel;
    }
    /**
     * Replace old floating panel content with one or multiple elements
     * @param {HTMLElement} elements
     */
    setFloatingPanelContent(...elements) {
      if (elements?.length) {
        this.getFloatingPanel().replaceChildren(...elements);
      } else {
        this.getFloatingPanel().replaceChildren();
      }
    }

    /**
     * Get first child element of floating panel
     * @return {IXMLDOMNodeList}
     */
    getFloatingPanelContent() {
      return this.getFloatingPanel().childNodes;
    }
    /**
     * Add element(s) in floating panel
     * @param {HTMLElement} elements
     */
    addFloatingPanelItems(...elements) {
      this.getFloatingPanel().append(...elements);
    }
    /**
     * Remove element from floating panel
     * @param {HTMLElement} element
     */
    removeFloatingPanelItem(element) {
      element.remove();
    }
    /**
     * Returns true if floating panel is currently visible
     * @returns {boolean|*}
     */
    isFloatingPanelVisible() {
      return this._isFloatingPanelVisible;
    }
    /**
     * Show floating panel
     * @param {string?} cssClass : needed at least for horizontal alignment
     * @param {Object?} inlineStyle : additional style rules
     */
    showFloatingPanel(cssClass, inlineStyle) {
      if (this.isFloatingPanelVisible()) {
        return; // if floating panel is already visible, do nothing
      }
      // Hide the drawer if not docked to avoid overlap with floating panel
      if (!this.isSideBarDrawerDocked()) {
        this.hideSideBarDrawer();
      }
      // update current floating panel list
      const floatingPanel = this.getFloatingPanel();
      this.getMain().append(floatingPanel);
      if (cssClass) {
        floatingPanel.classList.add(cssClass);
      }
      if (inlineStyle) {
        Object.assign(floatingPanel.style, inlineStyle);
      }
      floatingPanel.classList.remove("hidden");
      // role="dialog" is statically set in MainContainerWidget.tpl.html; pair it with aria-modal while visible.
      floatingPanel.setAttribute("aria-modal", "true");
      this.showBackDrop("floatingpanel");
      // add close button specifically for mobile
      if (cls.WindowHelper.isMobile()) {
        floatingPanel.append(this.getCloseIcon());
      }
      this._isFloatingPanelVisible = true;
      this._eventListener.emit(context.constants.widgetEvents.floatingPanelShown);
    }

    /**
     * Hide floating panel
     */
    hideFloatingPanel() {
      if (!this.isFloatingPanelVisible()) {
        return; // if floating panel is already hidden, do nothing
      }
      if (this._floatingPanelCloseHandler) {
        this._floatingPanelCloseHandler();
        this._floatingPanelCloseHandler = null;
      }
      const floatingPanel = this.getFloatingPanel();
      floatingPanel.className = "gbc_FloatingPanel";
      floatingPanel.removeAttribute("style");
      floatingPanel.removeAttribute("aria-modal");
      floatingPanel.remove();
      this.setFloatingPanelContent();
      this.hideBackDrop("floatingpanel");
      if (cls.WindowHelper.isMobile()) {
        floatingPanel.remove(this.getCloseIcon());
      }
      this._isFloatingPanelVisible = false;
      this._eventListener.emit(context.constants.widgetEvents.floatingPanelHidden);
    }

    /**
     * Handler to execute when floating panel is closed
     * {Function} handler
     */
    onFloatingPanelClose(handler) {
      this._floatingPanelCloseHandler = handler;
    }

    /**
     * Adds dock button on floating panel
     */
    addFloatingPanelDockButton() {
      // dock button
      if (this._floatingPanelDockItem) {
        this.getFloatingPanel().append(this._floatingPanelDockItem.getElement());
      }
    }

    /**
     * Adds undock button on docked panel
     */
    addDockedPanelUnDockButton() {
      // undock button
      if (this._dockedPanelUnDockItem) {
        this.getDockedPanel().append(this._dockedPanelUnDockItem.getElement());
      }
    }

    /**
     * Remove dock button on floating panel
     */
    removeFloatingPanelDockButton() {
      // dock button
      if (this._floatingPanelDockItem) {
        this._floatingPanelDockItem.getElement().remove();
      }
    }

    /**
     * Remove dock button on floating panel
     */
    removeDockedPanelUnDockButton() {
      // undock button
      if (this._dockedPanelUnDockItem) {
        this._dockedPanelUnDockItem.getElement().remove();
      }
    }

    /**
     * Return true if docked panel is visible
     * @return {boolean}
     */
    isDockedPanelVisible() {
      return this._isDockedPanelVisible;
    }

    /**
     * Return true if docked panel is empty
     */
    isDockedPanelEmpty() {
      // dock panel may contain undock button
      return this.getDockedPanel().childNodes.length <= 1;
    }

    /**
     * Add element(s) in docked panel
     * @param {HTMLElement} elements
     */
    addDockedPanelItems(...elements) {
      this.getDockedPanel().append(...elements);
    }
    /**
     * Remove element from docked panel
     * @param {HTMLElement} element
     */
    removeDockedPanelItem(element) {
      element.remove();
    }

    /**
     * Set content in docked panel
     * @param elements
     */
    setDockedPanelContent(...elements) {
      if (elements?.length) {
        this.getDockedPanel().replaceChildren(...elements);
      } else {
        this.getDockedPanel().replaceChildren();
      }
    }

    /**
     * Show docked panel
     */
    showDockedPanel() {
      this.getDockedPanel().classList.remove("hidden");
      this._initSplitter(this.getDockedPanel());
      this.addDockedPanelUnDockButton();
      this._isDockedPanelVisible = true;
    }

    /**
     * Hide docked panel
     */
    hideDockedPanel() {
      this.getDockedPanel().classList.add("hidden");
      this._destroySplitter();
      this.removeDockedPanelUnDockButton();
      this._isDockedPanelVisible = false;
    }

    /**
     * Move content of floating panel to docked panel
     * Also store the docking state in the stored settings
     */
    dockFloatingPanel() {
      if (this.isDockedPanelVisible()) {
        return;
      }
      if (this.isSideBarDrawerDocked()) {
        this.unDockSideBarDrawer();
        this.hideSideBarDrawer();
      }
      this.setDockedPanelContent(...this.getFloatingPanelContent());
      this.removeFloatingPanelDockButton();
      this.showDockedPanel();
      this.hideFloatingPanel();
      this.getDockedPanel().style.width = `${gbc.StoredSettingsService.loadSideBarWidth()}px`;
      // Store the state
      this._eventListener.emit(context.constants.widgetEvents.floatingPanelDocked);
      // need a relayout since we update available width
      context.SessionService.getCurrent()?.getCurrentApplication()?.scheduler.layoutCommand({ resize: true });
    }
    /**
     * Undock the floating panel and keep it displayed
     * Also store the docking state in the stored settings
     */
    unDockFloatingPanel() {
      this._hasFloatingPanelBeenUndocked = true;
      if (!this.isDockedPanelVisible()) {
        return;
      }
      this.setDockedPanelContent();
      this.hideDockedPanel();
      this._eventListener.emit(context.constants.widgetEvents.floatingPanelUndocked);
      // need a relayout since we update available width
      context.SessionService.getCurrent()?.getCurrentApplication()?.scheduler.layoutCommand({ resize: true });
    }

    /**
     * Returns the backdrop overlay element used behind panels/drawers
     * @return {{element: HTMLElement, active: boolean, visible: boolean, timer: Function}}
     */
    getBackDrop() {
      return context.OverlayService.get("maincontainer.overlay");
    }

    /**
     * Display backdrop over Main content
     */
    showBackDrop() {
      this.getBackDrop().element.classList.add("gbc_BackDrop");
      context.OverlayService.enable("maincontainer.overlay", this.getMain());
    }
    /**
     * Remove backdrop from Main content
     */
    hideBackDrop() {
      context.OverlayService.disable("maincontainer.overlay");
    }

    /**
     * Hide the FloatingPanel
     * and the SideBarDrawer if it's not docked
     */
    hideFloatingElements() {
      if (!this.isDockedPanelVisible()) {
        this.hideFloatingPanel();
      }
      if (!this.isSideBarDrawerDocked()) {
        this.hideSideBarDrawer();
      }
    }

    /**
     * Returns true if one of the floating element (sidebardrawer or floating panel) is currently visible
     */
    hasFloatingElements() {
      return this.isFloatingPanelVisible() || (this.isSideBarDrawerVisible() && !this.isSideBarDrawerDocked());
    }

    /**
     * Return or create close icon element
     * @return {HTMLElement}
     */
    getCloseIcon() {
      if (!this._closeIcon) {
        this._closeIcon = document.createElement("div");
        this._closeIcon.classList.add("zmdi", "close");
      }
      return this._closeIcon;
    }

    /**
     * Returns the undock button item
     * @returns {cls.ItemWidget}
     */
    getUndockItem() {
      return this._sideBarDrawerUnDockItem;
    }

    /**
     * Shows the open drawer button item
     */
    showOpenDrawerItem() {
      this._openDrawerItem?.removeClass("hidden");
    }

    /**
     * Hides the open drawer button item
     */
    hideOpenDrawerItem() {
      this._openDrawerItem?.addClass("hidden");
    }

    /**
     * Return main container
     * @returns {HTMLElement}
     */
    getMain() {
      if (!this._main) {
        this._main = window.document.getElementsByClassName("gbc_Main")[0];
      }
      return this._main;
    }

    // Event system
    when(eventName, hook) {
      return this._eventListener.when(eventName, hook);
    }
  }
  gbc.MainContainerService = new MainContainerService();
  context.InitService.register(context.MainContainerService);
});
